import React from 'react';
import { Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { Box, ButtonV2, CancelButton, Flex, FormControl, SpacingStack, TextArea, TextV2 } from '@withjoy/joykit';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useTranslation } from '@shared/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useFieldErrors } from '@shared/createEvent/hooks/useFieldErrors';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface RoomBlockCancelDialogProps {
  id: string;
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
  onCancelRoomBlock: (id: string, reason: string) => void;
}

export const RoomBlockCancelDialog: React.FC<RoomBlockCancelDialogProps> = props => {
  const { id, isOpen, loading, onClose, onCancelRoomBlock } = props;
  const { t2 } = useTranslation('websiteEditor');
  const cancelRoomBlock = t2('customPage', 'cancelRoomBlock');

  const formik = useFormik<{ reason?: string }>({
    initialValues: {
      reason: '',
    },
    validationSchema: Yup.object<{ reason: string }>({
      reason: Yup.string().required(cancelRoomBlock.inputError),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      onCancelRoomBlock(id, values.reason ?? '');
      formik.resetForm();
    },
  });
  const getFieldErrorProps = useFieldErrors<{ reason?: string }>(formik);

  const handleOnClose = useEventCallback(() => {
    formik.resetForm();
    onClose();
  });

  const submitForm = useEventCallback(() => {
    formik.handleSubmit();
  });

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleOnClose}
      hideDivider
      removeBodyPadding
      hasCloseButton
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingX: 6, paddingBottom: 8, paddingTop: 0 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', pxToRem(480)] } },
        Body: { props: { paddingX: 8, paddingY: 8 } },
        Header: { props: { paddingBottom: [0, 0] } },
      }}
    >
      <form onSubmit={submitForm}>
        <SpacingStack spacing={8}>
          <SpacingStack
            spacing={6}
            maxWidth={pxToRem(320)}
            marginX="auto"
          >
            <TextV2
              as="h3"
              typographyVariant="hed6"
              textAlign="center"
            >
              {cancelRoomBlock.title}
            </TextV2>
            <TextV2
              as="p"
              typographyVariant="body2"
              color="mono12"
              textAlign="center"
              fontSize={17}
              lineHeight="25.5px"
            >
              {cancelRoomBlock.message}
            </TextV2>
          </SpacingStack>
          <FormControl
            marginTop={2}
            isRequired={false}
            label={cancelRoomBlock.label}
            overrides={{ Label: { props: { textAlign: 'center' } } }}
            {...getFieldErrorProps('reason')}
          >
            <Flex
              __css={{
                ['& > div']: {
                  width: '100% !important',
                  height: '100%',
                },
                ['textarea']: {
                  minHeight: pxToRem(100),
                  maxHeight: pxToRem(400),
                  width: '100%',
                  minWidth: '100%',
                  outline: 0,
                  borderRadius: pxToRem(6),
                  border: '0px solid',
                  boxShadow: getFieldErrorProps('reason').isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 1px #D9D9D9',
                  _hover: { border: '0px solid', boxShadow: getFieldErrorProps('reason').isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 1px #333' },
                  _focus: { outline: 0, boxShadow: getFieldErrorProps('reason').isInvalid ? '0px 0 0px 2px #c50020' : '0px 0 0px 2px #333' },
                  color: 'mono14',
                  fontSize: '1rem !important',
                },
              }}
            >
              <TextArea
                minRows={3}
                resize={true}
                {...formik.getFieldProps('reason')}
              />
            </Flex>
          </FormControl>
          <Box width="100%">
            <ButtonV2
              intent="destructive"
              onClick={submitForm}
              width="100%"
              startIcon={<Trash />}
              loading={loading}
              disabled={loading}
            >
              {cancelRoomBlock.cancelRoomBlockCta}
            </ButtonV2>
            <CancelButton
              marginRight="auto"
              onClick={handleOnClose}
              width="100%"
              marginTop={5}
            >
              {cancelRoomBlock.cancelCta}
            </CancelButton>
          </Box>
        </SpacingStack>
      </form>
    </ResponsiveDialog>
  );
};
